import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useState, type Ref } from 'react';

import type { DocumentCategoryRow } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';

import { CategoryDialog } from './CategoryDialog';
import { useCategories } from './useDocuments';

/** The select's last option, which opens the category dialog rather than choosing. */
const NEW_CATEGORY = 'new';

interface CategorySelectFieldProps {
  /** A category id, or the empty string for Uncategorized. */
  value: string;
  onChange: (categoryId: string) => void;
  error?: string;
  /** The form's handle on the select, so a refusal can move focus to it. */
  inputRef?: Ref<HTMLInputElement>;
  /** Shown but not changeable, as on a removed document. */
  disabled?: boolean;
  /**
   * The `Field`'s test ID: `<testId>-select`, `<testId>-option-<id>`,
   * `<testId>-option-new`, and `<testId>-retry-button` when the categories
   * could not be loaded.
   */
  testId: string;
}

/**
 * The category dropdown of the upload dialog and the drawer (P9): Uncategorized
 * first, the organization's categories, and "New category…" last, which opens
 * `CategoryDialog` and selects what it created.
 *
 * Controlled rather than registered with the form, because the last option is
 * not a value: choosing it opens a dialog and leaves the select as it was.
 */
export function CategorySelectField({
  value,
  onChange,
  error,
  inputRef,
  disabled = false,
  testId,
}: CategorySelectFieldProps) {
  const categories = useCategories();
  const [creating, setCreating] = useState(0);

  // Until the categories arrive, or when they could not be read, the select
  // would offer only Uncategorized - so it waits, and says why.
  const unavailable = categories.isPending || categories.isError;
  const hint = categories.isPending
    ? 'Loading categories…'
    : categories.isError
      ? 'The categories could not be loaded.'
      : undefined;

  return (
    <>
      <Field label="Category" error={error} hint={hint} testId={testId}>
        <TextField
          select
          value={value}
          size="small"
          disabled={disabled || unavailable}
          inputRef={inputRef}
          onChange={(event) => {
            if (event.target.value === NEW_CATEGORY) {
              setCreating((n) => n + 1);
              return;
            }
            onChange(event.target.value);
          }}
        >
          <MenuItem value="">Uncategorized</MenuItem>
          {(categories.data?.categories ?? []).map((category) => (
            <MenuItem key={category.id ?? ''} value={category.id ?? ''}>
              {category.name}
            </MenuItem>
          ))}
          <MenuItem value={NEW_CATEGORY}>New category…</MenuItem>
        </TextField>
      </Field>
      {categories.isError ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void categories.refetch()}
          data-testid={`${testId}-retry-button`}
          sx={{ alignSelf: 'flex-start' }}
        >
          Load the categories again
        </Button>
      ) : null}

      {creating > 0 ? (
        <CategoryDialog
          key={creating}
          open
          onClose={() => setCreating(0)}
          onSaved={(row: DocumentCategoryRow) => onChange(row.id ?? '')}
        />
      ) : null}
    </>
  );
}
